import { useEffect, useMemo, useState } from 'react';

import { Button, Dropdown, Input, Label, TextArea } from '@components/index';
import { OrgStatus } from '@constants/generic';
import { cities } from '@features/admin/components/location-details/cityMapping';
import { useUpdateOrganizationDetailsMutation } from '@features/admin/queries/AdminQuery';
import useNotify from '@hooks/notify';
import { ApiError } from '@types';

type AddressProps = {
  addressLine1?: string;
  city?: string;
  pincode?: string;
  state?: string;
  organisationId?: string;
  currentUser: any;
  refetchOrganizationDetails: () => void;
  organizationData: any;
};

type AddressData = {
  addressLine1: string;
  city: string;
  pincode: string;
  state: string;
  organisationId: string;
};

const Address = ({
  addressLine1: initialAddressLine1 = '',
  city: initialCity = '',
  pincode: initialPincode = '',
  state: initialState = '',
  organisationId = '',
  currentUser,
  refetchOrganizationDetails,
  organizationData
}: AddressProps) => {
  const [formData, setFormData] = useState<AddressData>({
    addressLine1: initialAddressLine1,
    city: initialCity,
    pincode: initialPincode,
    state: initialState,
    organisationId
  });

  // Sync state with props when they change
  useEffect(() => {
    setFormData({
      addressLine1: initialAddressLine1,
      city: initialCity,
      pincode: initialPincode,
      state: initialState,
      organisationId
    });
  }, [
    initialAddressLine1,
    initialCity,
    initialPincode,
    initialState,
    organisationId
  ]);

  // Memoize state list to avoid recreating on every render
  const stateList = useMemo(
    () =>
      Object.keys(cities).map((stateName) => ({
        id: stateName,
        label: stateName,
        value: stateName
      })),
    []
  );

  const { createSuccessAlert, createErrorAlert } = useNotify();
  const [updateOrganizationDetails, { isLoading }] =
    useUpdateOrganizationDetailsMutation();

  const handleInputChange = (field: keyof AddressData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const shouldEnableFields = useMemo(
    () =>
      (!organizationData?.declaration_status &&
        organizationData?.status !== OrgStatus.APPROVED) ||
      organizationData?.declaration_status === 'submitted',
    [organizationData?.declaration_status, organizationData?.status]
  );

  const isAddressLine1Enabled = useMemo(
    () =>
      shouldEnableFields ||
      (organizationData?.status === OrgStatus.APPROVED &&
        !organizationData?.address_line_1),
    [
      organizationData?.address_line_1,
      organizationData?.status,
      shouldEnableFields
    ]
  );

  const isCityEnabled = useMemo(
    () =>
      shouldEnableFields ||
      (organizationData?.status === OrgStatus.APPROVED &&
        !organizationData?.city),
    [organizationData?.city, organizationData?.status, shouldEnableFields]
  );

  const isStateEnabled = useMemo(
    () =>
      shouldEnableFields ||
      (organizationData?.status === OrgStatus.APPROVED &&
        !organizationData?.state),
    [organizationData?.state, organizationData?.status, shouldEnableFields]
  );

  const isPincodeEnabled = useMemo(
    () =>
      shouldEnableFields ||
      (organizationData?.status === OrgStatus.APPROVED &&
        !organizationData?.pincode),
    [organizationData?.pincode, organizationData?.status, shouldEnableFields]
  );

  // Extract validation logic
  const validateForm = (): string | null => {
    if (!formData.addressLine1.trim()) {
      return 'Address Line 1 is required';
    }
    if (!formData.city.trim()) {
      return 'City is required';
    }
    if (!formData.state) {
      return 'State is required';
    }
    if (!formData.pincode.trim()) {
      return 'Pincode is required';
    }
    // Validate pincode is numeric and exactly 6 digits
    const pincodeRegex = /^\d{6}$/;
    if (!pincodeRegex.test(formData.pincode.trim())) {
      return 'Pincode must be exactly 6 digits';
    }
    if (!formData.organisationId) {
      return 'Organization ID is missing';
    }
    return null;
  };

  // Check if form data has changed from initial values
  const hasFormChanged = useMemo(
    () =>
      formData.addressLine1.trim() !== initialAddressLine1.trim() ||
      formData.city.trim() !== initialCity.trim() ||
      formData.pincode.trim() !== initialPincode.trim() ||
      formData.state !== initialState,
    [
      formData.addressLine1,
      formData.city,
      formData.pincode,
      formData.state,
      initialAddressLine1,
      initialCity,
      initialPincode,
      initialState
    ]
  );

  const handleSaveChanges = async () => {
    // Validate form
    const validationError = validateForm();
    if (validationError) {
      createErrorAlert(validationError);
      return;
    }

    // Check if data has changed
    if (!hasFormChanged) {
      createErrorAlert('No changes to save');
      return;
    }
    let payload = {};
    if (organizationData?.status === OrgStatus.APPROVED) {
      payload = {
        command: 'UPDATE_ORG_LOCATION',
        command_data: {
          organisation_id: formData.organisationId,
          ...(isAddressLine1Enabled && formData.addressLine1?.length > 0
            ? { address_line_1: formData.addressLine1.trim() }
            : {}),
          ...(isCityEnabled && formData.city?.length > 0
            ? { city: formData.city.trim() }
            : {}),
          ...(isStateEnabled && formData.state?.length > 0
            ? { state: formData.state }
            : {}),
          ...(isPincodeEnabled && formData.pincode?.length > 0
            ? { pincode: formData.pincode.trim() }
            : {})
        },
        note: 'Updating org location'
      };
    } else {
      payload = {
        command: 'UPDATE_ORG_LOCATION',
        command_data: {
          organisation_id: formData.organisationId,
          address_line_1: formData.addressLine1.trim(),
          city: formData.city.trim(),
          state: formData.state,
          pincode: formData.pincode.trim()
        },
        note: 'Updating org location'
      };
    }

    try {
      const response = await updateOrganizationDetails({
        body: payload,
        actorId: currentUser.mobile
      }).unwrap();

      if (response) {
        createSuccessAlert('Address updated successfully');
        // Update initial values to current values after successful save
        setFormData((prev) => ({
          ...prev,
          addressLine1: prev.addressLine1.trim(),
          city: prev.city.trim(),
          pincode: prev.pincode.trim()
        }));
        refetchOrganizationDetails();
      }
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage =
        apiError?.data?.errors?.[0]?.message ||
        apiError?.data?.message ||
        'Failed to update address. Please try again.';
      createErrorAlert(errorMessage);
    }
  };

  const handleCancel = () => {
    setFormData({
      addressLine1: initialAddressLine1,
      city: initialCity,
      pincode: initialPincode,
      state: initialState,
      organisationId
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="w-1/2">
          <Label label="Address Line 1" />
          <TextArea
            value={formData.addressLine1}
            onChange={(value) => handleInputChange('addressLine1', value)}
            placeholder="Enter address line 1"
            rows={4}
            inputStyle="!bg-theme"
            className="w-full"
            maxLength={150}
            disabled={!isAddressLine1Enabled}
          />
        </div>
        <div className="flex gap-4">
          <div className="w-1/2">
            <Label label="City" />
            <Input
              value={formData.city}
              handleInputChange={(value) => handleInputChange('city', value)}
              type="text"
              placeholder="Enter city"
              inputStyle="!bg-theme"
              className="w-full"
              disabled={!isCityEnabled}
            />
          </div>

          <div className="w-1/2">
            <Label label="Pincode" />
            <Input
              value={formData.pincode}
              handleInputChange={(value) => handleInputChange('pincode', value)}
              type="number"
              placeholder="Enter pincode"
              inputStyle="!bg-theme"
              className="w-full"
              maxLength={6}
              disabled={!isPincodeEnabled}
            />
          </div>
        </div>

        <div className="w-1/2">
          <Label label="State" />
          <Dropdown
            currentFilter={formData.state}
            filterOptions={stateList}
            disabled={!isStateEnabled}
            onSelectFilter={(value) => handleInputChange('state', value)}
            searchBarPlaceholder="Search state"
            className="h-12.5 w-full !rounded-lg !bg-theme"
            optionsStyle="!w-full max-h-56 overflow-y-auto"
            placeholder="Select State"
          />
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <Button
          label="Cancel"
          variant="secondary"
          handleClick={handleCancel}
          className="rounded-lg"
        />
        <Button
          label="Save changes"
          variant="primary"
          handleClick={handleSaveChanges}
          disabled={isLoading || !hasFormChanged}
          loading={isLoading}
          className="rounded-lg"
        />
      </div>
    </div>
  );
};

export default Address;
